import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Button, IconButton, TextField, Tooltip } from '@mui/material';
import { Close as CloseIcon, Check as CheckIcon, Clear as ClearIcon, DragIndicator, CallSplit as BranchIcon } from '@mui/icons-material';

const EditableScreenTab = ({
  screen,
  isActive,
  canDelete,
  onSelect,
  onRename,
  onDelete,
  hasRouting,
}) => {
  var [isEditing, setIsEditing] = useState(false);
  var [editValue, setEditValue] = useState(screen.title);

  var {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: screen.id,
    data: {
      type: 'screen-tab',
      screen,
    },
  });

  var style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  var handleDoubleClick = (e) => {
    e.stopPropagation();
    setIsEditing(true);
    setEditValue(screen.title);
  };

  var handleSaveEdit = () => {
    if (editValue.trim()) {
      onRename(screen.id, editValue.trim());
    }
    setIsEditing(false);
  };

  var handleCancelEdit = () => {
    setEditValue(screen.title);
    setIsEditing(false);
  };

  var handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  var handleDelete = (e) => {
    e.stopPropagation();
    onDelete(screen.id);
  };

  if (isEditing) {
    return (
      <Box ref={setNodeRef} style={style} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <TextField
          size="small"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSaveEdit}
          autoFocus
          sx={{
            minWidth: 120,
            '& .MuiInputBase-root': {
              height: 32,
              fontSize: '0.875rem'
            }
          }}
        />
        <IconButton
          size="small"
          onClick={handleSaveEdit}
          sx={{ p: 0.25 }}
        >
          <CheckIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={handleCancelEdit}
          sx={{ p: 0.25 }}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      ref={setNodeRef}
      style={style}
      className={`screen-tab ${isDragging ? 'screen-tab--dragging' : ''}`}
      sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}
    >
      <IconButton
        {...attributes}
        {...listeners}
        size="small"
        className="screen-tab__drag-handle"
        sx={{
          p: 0.25,
          cursor: 'grab',
          color: 'text.secondary',
          opacity: 0.6,
          '&:hover': { opacity: 1 },
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <DragIndicator fontSize="small" />
      </IconButton>

      <Tooltip title={screen.title || ''}>
        <Button
          variant={isActive ? "contained" : "outlined"}
          size="small"
          onClick={() => onSelect(screen.id)}
          onDoubleClick={handleDoubleClick}
          sx={{ minWidth: 80, maxWidth: 200 }}
          endIcon={
            hasRouting ? (
              <Tooltip title="Has routing rules">
                <BranchIcon sx={{ fontSize: '0.85rem !important', opacity: 0.8 }} />
              </Tooltip>
            ) : undefined
          }
        >
          <Box
            component="span"
            className="screen-tab__title"
            sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {screen.title}
          </Box>
        </Button>
      </Tooltip>

      {canDelete && (
        <IconButton
          size="small"
          onClick={handleDelete}
          sx={{
            p: 0.25,
            ml: 0.25,
            opacity: 0.6,
            '&:hover': { opacity: 1 },
            '& .MuiSvgIcon-root': {
              fontSize: '1rem'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
    </Box>
  );
};

export default EditableScreenTab;
